import { Badge, Button, Checkbox, Input, Typography } from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { MassTortSurveyMatterSelectionTable } from "@/components/features/mass-torts/MassTortSurveyMatterSelectionTable";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { retrieveMassTortMatterSearchQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import {
  MATTER_STATUS_CATEGORY_DISPLAY_LABELS,
  MATTER_STATUS_CATEGORY_OPTIONS,
} from "@/lib/data/static/matterStatics";
import type {
  MassTortMatterSearch,
  ReportsMatterSearchTableRow,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { mapMassTortMatterSearchToTableRow } from "@/lib/utils/matters/mass-tort-matter-search-utils";
import { useQueries } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

function getMatterStatusCategoryLabel(category?: string | null) {
  if (category && category in MATTER_STATUS_CATEGORY_DISPLAY_LABELS) {
    return MATTER_STATUS_CATEGORY_DISPLAY_LABELS[
      category as keyof typeof MATTER_STATUS_CATEGORY_DISPLAY_LABELS
    ];
  }

  return category ?? "";
}

export function SelectedMattersAccordion({
  selectedMatters,
}: {
  selectedMatters: ReportsMatterSearchTableRow[];
}) {
  const selectedMatterCount = selectedMatters.length;

  if (selectedMatterCount === 0) {
    return null;
  }

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem
        value="selected-matters"
        className="border-border rounded-lg border last:border-b"
      >
        <AccordionTrigger className="px-4 py-3 hover:no-underline">
          {`Selected (${selectedMatterCount})`}
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="flex flex-wrap gap-1.5">
            {selectedMatters.map((selectedMatter) => {
              const matterTitle =
                selectedMatter.matterTitle?.trim() || "Untitled Matter";

              return (
                <Badge
                  key={selectedMatter.matterId}
                  variant="outline"
                  className="max-w-full rounded-md font-normal whitespace-normal"
                >
                  {matterTitle}
                </Badge>
              );
            })}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export function MassTortMatterSelectionModal({
  open,
  setOpen,
  massTortId,
  selectedMatters,
  setSelectedMatters,
  purpose = "file-generation",
  defaultSelectAll = false,
  onConfirm,
  confirmLabel,
  confirmPending = false,
  confirmDisabled = false,
  confirmError,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  massTortId: string;
  selectedMatters: ReportsMatterSearchTableRow[];
  setSelectedMatters: Dispatch<SetStateAction<ReportsMatterSearchTableRow[]>>;
  purpose?: "file-generation" | "survey-distribution";
  defaultSelectAll?: boolean;
  onConfirm?: (allMattersSelected: boolean) => void;
  confirmLabel?: string;
  confirmPending?: boolean;
  confirmDisabled?: boolean;
  confirmError?: string;
}) {
  const isSurveyDistribution = purpose === "survey-distribution";
  const matterQueries = useQueries({
    queries: MATTER_STATUS_CATEGORY_OPTIONS.map((category) => ({
      ...retrieveMassTortMatterSearchQueryOptions({
        massTortId,
        statusCategory: category,
      }),
      enabled: open && Boolean(massTortId),
    })),
  });
  const [searchQuery, setSearchQuery] = useState("");
  const defaultSelectionApplied = useRef(false);
  const selectedMatterIds = useMemo(
    () => new Set(selectedMatters.map((matter) => matter.matterId)),
    [selectedMatters],
  );
  const matters = useMemo(() => {
    const matterSearches = matterQueries.flatMap(
      (query) => query.data?.data ?? [],
    );
    const matterRowsById = new Map<string, ReportsMatterSearchTableRow>();

    matterSearches.forEach((matter: MassTortMatterSearch) => {
      const row = mapMassTortMatterSearchToTableRow(matter);
      if (matterRowsById.has(row.matterId)) {
        return;
      }
      matterRowsById.set(row.matterId, row);
    });

    return Array.from(matterRowsById.values()).sort((matterA, matterB) =>
      (matterA.matterTitle ?? "").localeCompare(matterB.matterTitle ?? ""),
    );
  }, [matterQueries]);
  const filteredMatters = useMemo(() => {
    const sanitizedQuery = searchQuery.trim().toLowerCase();
    if (!sanitizedQuery) {
      return matters;
    }

    return matters.filter((matter) =>
      [
        matter.matterTitle,
        matter.matterStatusLabel,
        matter.incidentTypeLabel,
        matter.matterContactConnection[0]?.matterContactConnectionContactName,
        ...(isSurveyDistribution
          ? [
              ...(matter.matterContactConnection[0]?.matterContactConnectionContactEmail?.map(
                (email) => email.address,
              ) ?? []),
              ...(matter.matterContactConnection[0]?.matterContactConnectionContactPhone?.map(
                (phone) => phone.number,
              ) ?? []),
            ]
          : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(sanitizedQuery),
    );
  }, [isSurveyDistribution, matters, searchQuery]);
  const filteredMatterIds = useMemo(
    () => new Set(filteredMatters.map((matter) => matter.matterId)),
    [filteredMatters],
  );
  const isSearchActive = searchQuery.trim().length > 0;
  const allFilteredMattersSelected =
    filteredMatters.length > 0 &&
    filteredMatters.every((matter) => selectedMatterIds.has(matter.matterId));
  const someFilteredMattersSelected = filteredMatters.some((matter) =>
    selectedMatterIds.has(matter.matterId),
  );
  const filteredSelectionState: boolean | "indeterminate" =
    allFilteredMattersSelected
      ? true
      : someFilteredMattersSelected
        ? "indeterminate"
        : false;
  const isLoading = matterQueries.some(
    (query) => query.isLoading || query.isFetching,
  );
  const isError = matterQueries.some((query) => query.isError);
  const allMattersSelected =
    matters.length > 0 &&
    matters.every((matter) => selectedMatterIds.has(matter.matterId));

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      defaultSelectionApplied.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (
      !open ||
      !defaultSelectAll ||
      defaultSelectionApplied.current ||
      isLoading ||
      isError
    ) {
      return;
    }
    defaultSelectionApplied.current = true;
    setSelectedMatters(matters);
  }, [defaultSelectAll, isError, isLoading, matters, open, setSelectedMatters]);

  const toggleMatterSelection = useCallback(
    (matter: ReportsMatterSearchTableRow) => {
      setSelectedMatters((prev) => {
        const alreadySelected = prev.some(
          (selectedMatter) => selectedMatter.matterId === matter.matterId,
        );
        if (alreadySelected) {
          return prev.filter(
            (selectedMatter) => selectedMatter.matterId !== matter.matterId,
          );
        }
        return [...prev, matter];
      });
    },
    [setSelectedMatters],
  );
  const toggleAllFilteredMattersSelection = useCallback(() => {
    setSelectedMatters((prev) => {
      if (allFilteredMattersSelected) {
        return prev.filter(
          (selectedMatter) => !filteredMatterIds.has(selectedMatter.matterId),
        );
      }

      const existingMatterIds = new Set(prev.map((matter) => matter.matterId));
      const mattersToAdd = filteredMatters.filter(
        (matter) => !existingMatterIds.has(matter.matterId),
      );

      return [...prev, ...mattersToAdd];
    });
  }, [
    allFilteredMattersSelected,
    filteredMatterIds,
    filteredMatters,
    setSelectedMatters,
  ]);
  const selectAllLabel = isSearchActive
    ? `Select all filtered (${filteredMatters.length})`
    : `Select all (${filteredMatters.length})`;

  if (isSurveyDistribution) {
    return (
      <AppModal
        open={open}
        onOpenChange={(nextOpen) => {
          if (!confirmPending) setOpen(nextOpen);
        }}
        title="Select matters"
        description="Choose the matters that should receive a copy of this survey."
        size="4xl"
        className="max-h-[min(85vh,820px)] w-[calc(100vw-2rem)]"
        bodyClassName="min-h-0 px-0"
        bodyContained
        loading={confirmPending}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={confirmPending}
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                confirmPending ||
                confirmDisabled ||
                selectedMatters.length === 0 ||
                isLoading ||
                isError
              }
              onClick={() => onConfirm?.(allMattersSelected)}
            >
              {confirmPending
                ? "Distributing..."
                : (confirmLabel ??
                  `Distribute surveys (${selectedMatters.length})`)}
            </Button>
          </div>
        }
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-6 py-4">
          {confirmError ? (
            <p role="alert" className="text-destructive text-sm">
              {confirmError}
            </p>
          ) : null}
          <Input
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
            }}
            placeholder="Search matters..."
          />
          <div className="min-h-0 flex-1 overflow-auto">
            <MassTortSurveyMatterSelectionTable
              matters={filteredMatters}
              selectedMatterIds={selectedMatterIds}
              onToggleMatter={toggleMatterSelection}
              onToggleAll={toggleAllFilteredMattersSelection}
              isLoading={isLoading}
              isError={isError}
            />
          </div>
        </div>
      </AppModal>
    );
  }

  return (
    <AppModal
      open={open}
      className="h-[min(85vh,820px)] w-[calc(100vw-2rem)]"
      onOpenChange={(nextOpen) => {
        if (!confirmPending) setOpen(nextOpen);
      }}
      title="Select matters"
      description="Choose specific matters for this generation request. Leave all matters unselected to generate files for the entire mass tort."
      size="4xl"
      bodyClassName="min-h-0 px-0"
      bodyContained
      footer={
        <div className="flex w-full flex-wrap justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={selectedMatters.length === 0}
            onClick={() => {
              setSelectedMatters([]);
            }}
          >
            Clear selection
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={confirmPending}
              onClick={() => {
                setOpen(false);
              }}
            >
              Close
            </Button>
            <Button
              type="button"
              disabled={confirmPending || confirmDisabled}
              onClick={() => {
                if (onConfirm) onConfirm(allMattersSelected);
                else setOpen(false);
              }}
            >
              {confirmPending
                ? "Creating..."
                : (confirmLabel ?? `Select (${selectedMatters.length})`)}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4">
        <div className="border-border shrink-0 space-y-4 border-b py-4">
          <Typography className="text-muted-foreground text-sm">
            Select matters from the list below. Leave all matters unselected to
            include the entire mass tort.
          </Typography>
          {confirmError ? (
            <p role="alert" className="text-destructive text-sm">
              {confirmError}
            </p>
          ) : null}
          <Input
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
            }}
            placeholder="Search matters..."
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto py-4">
          {isLoading ? (
            <div
              className="divide-border overflow-hidden rounded-lg border"
              role="status"
              aria-label="Loading matters"
            >
              {[0, 1, 2].map((row) => (
                <div
                  key={row}
                  className="flex animate-pulse items-center gap-3 border-b px-4 py-3 last:border-b-0 motion-reduce:animate-none"
                  aria-hidden="true"
                >
                  <div className="bg-muted size-4 rounded" />
                  <div className="flex-1 space-y-2">
                    <div className="bg-muted h-4 w-2/5 rounded" />
                    <div className="bg-muted h-3 w-1/3 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="bg-muted/25 border-border rounded-lg border border-dashed p-4">
              <Typography className="text-destructive text-sm">
                Unable to load matters.
              </Typography>
            </div>
          ) : filteredMatters.length ? (
            <div className="divide-border divide-y overflow-hidden rounded-lg border">
              <div
                role="button"
                tabIndex={0}
                className="bg-muted/30 hover:bg-muted/50 flex w-full cursor-pointer items-center gap-3 border-b px-4 py-2.5 text-left transition-colors"
                onClick={toggleAllFilteredMattersSelection}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") {
                    return;
                  }
                  event.preventDefault();
                  toggleAllFilteredMattersSelection();
                }}
              >
                <Checkbox
                  checked={filteredSelectionState}
                  className="mt-0"
                  aria-label={selectAllLabel}
                  onCheckedChange={toggleAllFilteredMattersSelection}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                />
                <Typography className="text-sm font-medium">
                  {selectAllLabel}
                </Typography>
              </div>
              {filteredMatters.map((matter) => {
                const matterTitle =
                  matter.matterTitle?.trim() || "Untitled Matter";
                const isSelected = selectedMatterIds.has(matter.matterId);
                const clientName =
                  matter.matterContactConnection[0]
                    ?.matterContactConnectionContactName;

                return (
                  <div
                    key={matter.matterId}
                    role="button"
                    tabIndex={0}
                    className={cn(
                      "hover:bg-muted/60 flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors",
                      isSelected && "bg-primary/5",
                    )}
                    onClick={() => {
                      toggleMatterSelection(matter);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") {
                        return;
                      }
                      event.preventDefault();
                      toggleMatterSelection(matter);
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      className="mt-1"
                      aria-label={`Select ${matterTitle}`}
                      onCheckedChange={() => {
                        toggleMatterSelection(matter);
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <Typography className="truncate font-medium">
                        {matterTitle}
                      </Typography>
                      <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-sm">
                        {clientName ? <span>Client: {clientName}</span> : null}
                        {matter.matterStatusLabel ? (
                          <span>Status: {matter.matterStatusLabel}</span>
                        ) : matter.matterStatusCategory ? (
                          <span>
                            Status:{" "}
                            {getMatterStatusCategoryLabel(
                              matter.matterStatusCategory,
                            )}
                          </span>
                        ) : null}
                        {matter.incidentTypeLabel ? (
                          <span>Type: {matter.incidentTypeLabel}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-muted/25 border-border rounded-lg border border-dashed p-4">
              <Typography className="text-muted-foreground text-sm">
                No matters found.
              </Typography>
            </div>
          )}
        </div>
      </div>
    </AppModal>
  );
}
